import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Save, Eye, Upload, X, Check, Clock, AlertCircle, Star, Trash2, MoveUp, MoveDown, Image as ImageIcon } from 'lucide-react';

const ProductEditor = ({ 
  initialData = null, 
  onSave, 
  onCancel, 
  isEditing = false,
  onPreview 
}) => {
  // 初始表单数据
  const initialFormData = {
    name: '',
    description: '',
    full_description: '',
    price_info: '面议',
    features: '',
    specifications: '',
    applications: '',
    support_info: '',
    status: 'draft', // 使用新的状态字段：draft, published, unpublished
    images: [], // 改为数组，支持多图
    mainImageIndex: 0, // 主图索引
    contact_image: '' // 联系方式二维码图片URL
  };

  const [formData, setFormData] = useState(() => {
    const data = initialData || initialFormData;
    // 确保images字段总是存在且是数组
    return {
      ...initialFormData,
      ...data,
      images: data.images || [],
      mainImageIndex: data.mainImageIndex || 0
    };
  });
  const [uploading, setUploading] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // idle, saving, saved, error
  const [lastSaved, setLastSaved] = useState(null);
  const [draftId, setDraftId] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [imagesToDelete, setImagesToDelete] = useState([]); // 新增：跟踪要删除的图片ID
  const autoSaveTimerRef = useRef(null);
  const lastFormDataRef = useRef(formData);

  // 富文本编辑器配置
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'align',
    'link', 'image'
  ];

  // 初始化时加载草稿
  useEffect(() => {
    loadDraft();
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  // 检测表单变化
  useEffect(() => {
    const hasChanged = JSON.stringify(formData) !== JSON.stringify(lastFormDataRef.current);
    setHasChanges(hasChanged);
    
    if (hasChanged) {
      // 设置自动保存定时器（每分钟保存一次）
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      
      autoSaveTimerRef.current = setTimeout(() => {
        saveDraft();
      }, 60000); // 1分钟
    }

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [formData]);

  // 加载草稿
  const loadDraft = () => {
    try {
      const savedDraft = localStorage.getItem('product_draft');
      if (savedDraft) {
        const draftData = JSON.parse(savedDraft);
        
        // 转换JSON字段为富文本字符串
        const richTextFields = ['price_info', 'features', 'specifications', 'applications', 'support_info'];
        const processedFormData = { ...draftData.formData };
        
        richTextFields.forEach(field => {
          // 检查字段是否存在（包括空值）
          if (processedFormData[field] !== undefined && processedFormData[field] !== null) {
            processedFormData[field] = convertJsonToRichText(processedFormData[field]);
          }
        });
        
        // 确保图片字段正确设置
        const formDataWithImages = {
          ...processedFormData,
          images: processedFormData.images || [],
          mainImageIndex: processedFormData.mainImageIndex || 0
        };
        
        setFormData(formDataWithImages);
        setDraftId(draftData.draftId);
        setLastSaved(new Date(draftData.savedAt));
        console.log('草稿加载成功:', draftData);
      }
    } catch (error) {
      console.error('加载草稿失败:', error);
    }
  };

  // 保存草稿到本地存储
  const saveDraft = async () => {
    if (!hasChanges) return;

    setAutoSaveStatus('saving');
    try {
      // 序列化前移除File对象（File对象无法被JSON序列化）
      const formDataForStorage = {
        ...formData,
        images: (formData.images || []).map(img => ({
          image_url: img.image_url,
          thumbnail_url: img.thumbnail_url,
          name: img.name,
          size: img.size,
          type: img.type,
          isMain: img.isMain,
          isNew: img.isNew
        }))
      };
      
      const draftData = {
        formData: formDataForStorage,
        draftId: draftId || Date.now().toString(),
        savedAt: new Date().toISOString(),
        isDraft: true
      };

      localStorage.setItem('product_draft', JSON.stringify(draftData));
      setDraftId(draftData.draftId);
      setLastSaved(new Date());
      setAutoSaveStatus('saved');
      
      // 3秒后重置状态
      setTimeout(() => {
        setAutoSaveStatus('idle');
      }, 3000);

      console.log('草稿保存成功:', draftData);
    } catch (error) {
      console.error('保存草稿失败:', error);
      setAutoSaveStatus('error');
    }
  };

  // 手动保存草稿
  const handleSaveDraft = () => {
    saveDraft();
  };

  // 清除草稿
  const handleClearDraft = () => {
    localStorage.removeItem('product_draft');
    setDraftId(null);
    setLastSaved(null);
    setFormData(initialFormData);
    setHasChanges(false);
  };

  // 处理输入变化
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // 处理富文本编辑器变化
  const handleRichTextChange = (value, field) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 处理多图上传
  const handleImagesUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    try {
      setUploading(true);
      
      // 限制最多10张图片
      const maxImages = 10;
      const currentImages = formData?.images?.length || 0;
      const remainingSlots = maxImages - currentImages;
      
      if (files.length > remainingSlots) {
        alert(`最多只能上传${maxImages}张图片，当前已有${currentImages}张，还可以上传${remainingSlots}张`);
        files.splice(remainingSlots);
      }
      
      // 获取token
      const token = localStorage.getItem('token');
      
      // 上传每张图片
      const uploadedImages = [];
      for (const file of files) {
        // 创建上传用的FormData对象
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        
        // 上传图片到服务器
        const response = await fetch('/api/files/upload', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: uploadFormData
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            // 修复URL构建逻辑：file_path已经是相对路径（如products/2025/12/28/xxx.jpg）
            // 应该直接使用 /products/... 而不是 /uploads/products/...
            const filePath = result.data.file_path;
            const imageUrl = `/${filePath}`; // 直接使用file_path作为URL路径
            uploadedImages.push({
              image_url: imageUrl,
              thumbnail_url: imageUrl, // 新上传的图片暂时没有缩略图，使用原图
              file: file,
              name: file.name,
              size: file.size,
              type: file.type,
              isMain: false, // 默认不是主图
              isNew: true // 标记为新上传的图片
            });
          } else {
            throw new Error(result.message || '图片上传失败');
          }
        } else {
          throw new Error('图片上传失败');
        }
      }
      
      // 更新表单数据
      setFormData(prev => ({
        ...prev,
        images: [...(prev.images || []), ...uploadedImages]
      }));
      
      alert(`成功上传 ${uploadedImages.length} 张图片！`);
      
      // 立即保存草稿
      saveDraft();
      
    } catch (error) {
      console.error('图片上传失败:', error);
      alert(`图片上传失败: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  // 设置主图
  const setMainImage = (index) => {
    setFormData(prev => ({
      ...prev,
      mainImageIndex: index
    }));
  };

  // 处理联系方式图片上传
  const handleContactImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const token = localStorage.getItem('token');
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await fetch('/api/files/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadFormData
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          const filePath = result.data.file_path;
          const imageUrl = `/${filePath}`;
          setFormData(prev => ({
            ...prev,
            contact_image: imageUrl
          }));
        } else {
          throw new Error(result.message || '上传失败');
        }
      } else {
        throw new Error('上传失败');
      }
    } catch (error) {
      console.error('联系方式图片上传失败:', error);
      alert(`上传失败: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  // 移除联系方式图片
  const removeContactImage = () => {
    setFormData(prev => ({
      ...prev,
      contact_image: ''
    }));
  };

  // 删除图片
  const deleteImage = (index) => {
    setFormData(prev => {
      const newImages = [...(prev.images || [])];
      const deletedImage = newImages[index];
      
      // 如果删除的是已有图片（有ID），添加到imagesToDelete列表
      if (deletedImage && deletedImage.id && !deletedImage.isNew) {
        setImagesToDelete(prevIds => [...prevIds, deletedImage.id]);
      }
      
      newImages.splice(index, 1);
      
      // 如果删除的是主图，将第一张图设为主图
      let newMainIndex = prev.mainImageIndex;
      if (index === prev.mainImageIndex) {
        newMainIndex = 0;
      } else if (index < prev.mainImageIndex) {
        newMainIndex = prev.mainImageIndex - 1;
      }
      
      return {
        ...prev,
        images: newImages,
        mainImageIndex: newMainIndex
      };
    });
  };

  // 上移图片
  const moveImageUp = (index) => {
    if (index <= 0) return;
    
    setFormData(prev => {
      const newImages = [...(prev.images || [])];
      [newImages[index], newImages[index - 1]] = [newImages[index - 1], newImages[index]];
      
      // 更新主图索引
      let newMainIndex = prev.mainImageIndex;
      if (prev.mainImageIndex === index) {
        newMainIndex = index - 1;
      } else if (prev.mainImageIndex === index - 1) {
        newMainIndex = index;
      }
      
      return {
        ...prev,
        images: newImages,
        mainImageIndex: newMainIndex
      };
    });
  };

  // 下移图片
  const moveImageDown = (index) => {
    if (index >= (formData?.images?.length || 0) - 1) return;
    
    setFormData(prev => {
      const newImages = [...(prev.images || [])];
      [newImages[index], newImages[index + 1]] = [newImages[index + 1], newImages[index]];
      
      // 更新主图索引
      let newMainIndex = prev.mainImageIndex;
      if (prev.mainImageIndex === index) {
        newMainIndex = index + 1;
      } else if (prev.mainImageIndex === index + 1) {
        newMainIndex = index;
      }
      
      return {
        ...prev,
        images: newImages,
        mainImageIndex: newMainIndex
      };
    });
  };

  // 将富文本内容转换为JSON格式（与后端期望的格式一致）
  const convertRichTextToJson = (htmlContent) => {
    // 始终返回有效的JSON对象，即使内容为空
    // 后端期望的格式：包含content字段的对象
    return {
      content: htmlContent || ''
    };
  };

  // 将JSON格式转换为富文本内容 - 修复版，处理数组和其他数据类型
  const convertJsonToRichText = (jsonData) => {
    if (!jsonData) return '';
    
    try {
      // 处理字符串类型
      if (typeof jsonData === 'string') {
        try {
          // 尝试解析JSON字符串
          const parsed = JSON.parse(jsonData);
          
          // 如果解析后是数组，转换为字符串表示
          if (Array.isArray(parsed)) {
            return parsed.join(', ');
          }
          
          // 如果解析后是对象
          if (parsed && typeof parsed === 'object') {
            // 如果是空对象，返回空字符串
            if (Object.keys(parsed).length === 0) {
              return '';
            }
            
            // 如果有content属性，返回content
            if (parsed.content !== undefined) {
              return String(parsed.content);
            }
            
            // 其他对象转换为JSON字符串
            return JSON.stringify(parsed);
          }
          
          // 其他类型直接转换为字符串
          return String(parsed);
        } catch {
          // 如果不是JSON格式，直接返回原字符串
          return jsonData;
        }
      }
      
      // 处理数组类型
      if (Array.isArray(jsonData)) {
        return jsonData.join(', ');
      }
      
      // 处理对象类型
      if (jsonData && typeof jsonData === 'object') {
        // 如果是空对象，返回空字符串
        if (Object.keys(jsonData).length === 0) {
          return '';
        }
        
        // 如果有content属性，返回content
        if (jsonData.content !== undefined) {
          return String(jsonData.content);
        }
        
        // 其他对象转换为JSON字符串
        try {
          return JSON.stringify(jsonData);
        } catch {
          return '';
        }
      }
      
      // 处理其他类型（数字、布尔值等）
      return String(jsonData);
    } catch (error) {
      console.error('转换JSON到富文本失败:', error, jsonData);
      return '';
    }
  };

  // 准备表单数据用于保存（保存为草稿）
  const prepareFormDataForSave = () => {
    const formDataToSend = new FormData();
    
    // 添加基本字段
    formDataToSend.append('name', formData.name);
    formDataToSend.append('description', formData.description);
    formDataToSend.append('full_description', formData.full_description);
    formDataToSend.append('status', 'draft'); // 草稿状态
    formDataToSend.append('mainImageIndex', formData.mainImageIndex.toString());
    
    // 处理富文本字段，转换为JSON格式
    const richTextFields = ['price_info', 'features', 'specifications', 'applications', 'support_info'];
    
    richTextFields.forEach(field => {
      const jsonData = convertRichTextToJson(formData[field]);
      // 现在jsonData永远不会为null，总是有效的JSON对象
      formDataToSend.append(field, JSON.stringify(jsonData));
    });
    
    // 添加联系方式图片URL
    formDataToSend.append('contact_image', formData.contact_image || '');
    
    // 准备existingImages参数（已有图片的信息）
    const existingImages = (formData.images || [])
      .filter(image => image.id && !image.isNew) // 过滤出已有图片（有ID且不是新上传的）
      .map((image, index) => ({
        id: image.id,
        display_order: index,
        is_main: index === formData.mainImageIndex
      }));
    
    if (existingImages.length > 0) {
      formDataToSend.append('existingImages', JSON.stringify(existingImages));
    }
    
    // 添加imagesToDelete参数
    if (imagesToDelete.length > 0) {
      formDataToSend.append('imagesToDelete', JSON.stringify(imagesToDelete));
    }
    
    // 添加新上传的图片文件
    (formData.images || []).forEach((image, index) => {
      if (image.file && image.isNew) {
        formDataToSend.append('images', image.file);
      }
    });
    
    return formDataToSend;
  };

  // 准备表单数据用于发布
  const prepareFormDataForPublish = () => {
    const formDataToSend = new FormData();
    
    // 添加基本字段
    formDataToSend.append('name', formData.name);
    formDataToSend.append('description', formData.description);
    formDataToSend.append('full_description', formData.full_description);
    formDataToSend.append('status', 'published'); // 发布状态
    formDataToSend.append('mainImageIndex', formData.mainImageIndex.toString());
    
    // 处理富文本字段，转换为JSON格式
    const richTextFields = ['price_info', 'features', 'specifications', 'applications', 'support_info'];
    
    richTextFields.forEach(field => {
      const jsonData = convertRichTextToJson(formData[field]);
      // 现在jsonData永远不会为null，总是有效的JSON对象
      formDataToSend.append(field, JSON.stringify(jsonData));
    });
    
    // 添加联系方式图片URL
    formDataToSend.append('contact_image', formData.contact_image || '');
    
    // 准备existingImages参数（已有图片的信息）
    const existingImages = (formData.images || [])
      .filter(image => image.id && !image.isNew) // 过滤出已有图片（有ID且不是新上传的）
      .map((image, index) => ({
        id: image.id,
        display_order: index,
        is_main: index === formData.mainImageIndex
      }));
    
    if (existingImages.length > 0) {
      formDataToSend.append('existingImages', JSON.stringify(existingImages));
    }
    
    // 添加imagesToDelete参数
    if (imagesToDelete.length > 0) {
      formDataToSend.append('imagesToDelete', JSON.stringify(imagesToDelete));
    }
    
    // 添加新上传的图片文件
    (formData.images || []).forEach((image, index) => {
      if (image.file && image.isNew) {
        formDataToSend.append('images', image.file);
      }
    });
    
    return formDataToSend;
  };

  // 处理表单提交
  const handleSubmit = async (e, publish = false) => {
    e.preventDefault();
    setUploading(true);

    try {
      // 准备表单数据
      const formDataToSend = publish ? prepareFormDataForPublish() : prepareFormDataForSave();

      // 调用父组件的保存函数
      await onSave(formDataToSend, publish);

      // 保存成功后清除草稿（无论是保存草稿还是发布都应该清除）
      localStorage.removeItem('product_draft');

    } catch (error) {
      console.error('保存失败:', error);
      alert('保存失败，请重试');
    } finally {
      setUploading(false);
    }
  };

  // 生成预览数据
  const generatePreviewData = () => {
    // 序列化前移除File对象
    const formDataForPreview = {
      ...formData,
      images: (formData.images || []).map(img => ({
        image_url: img.image_url,
        thumbnail_url: img.thumbnail_url,
        name: img.name,
        size: img.size,
        type: img.type,
        isMain: img.isMain,
        isNew: img.isNew
      }))
    };
    
    const previewData = {
      ...formDataForPreview,
      previewTime: new Date().toISOString(),
      isPreview: true,
      previewId: draftId || `preview-${Date.now()}`
    };
    
    // 将预览数据保存到localStorage，供预览页面使用
    localStorage.setItem('product_preview', JSON.stringify(previewData));
    
    return previewData;
  };

  // 处理预览
  const handlePreview = () => {
    const previewData = generatePreviewData();
    
    // 在新窗口打开预览页面
    const previewWindow = window.open('/preview/product', '_blank');
    
    if (!previewWindow) {
      alert('请允许弹出窗口以查看预览');
      return;
    }
    
    // 设置一个定时器，等待预览窗口加载完成
    const checkWindowLoaded = setInterval(() => {
      try {
        if (previewWindow.document.readyState === 'complete') {
          clearInterval(checkWindowLoaded);
          // 可以在这里向预览窗口发送数据，但更简单的方式是让预览页面从localStorage读取
        }
      } catch (e) {
        // 跨域安全限制，无法直接访问
      }
    }, 100);
    
    if (onPreview) {
      onPreview(previewData);
    }
  };

  // 自动保存状态显示
  const renderAutoSaveStatus = () => {
    switch (autoSaveStatus) {
      case 'saving':
        return (
          <div className="flex items-center text-blue-600 text-sm">
            <Clock size={14} className="mr-1" />
            自动保存中...
          </div>
        );
      case 'saved':
        return (
          <div className="flex items-center text-green-600 text-sm">
            <Check size={14} className="mr-1" />
            已自动保存
            {lastSaved && (
              <span className="ml-1 text-gray-500">
                ({new Date(lastSaved).toLocaleTimeString()})
              </span>
            )}
          </div>
        );
      case 'error':
        return (
          <div className="flex items-center text-red-600 text-sm">
            <AlertCircle size={14} className="mr-1" />
            保存失败
          </div>
        );
      default:
        return null;
    }
  };

  // 图片缩略图显示组件
  const renderImageThumbnails = () => {
    // 安全检查：确保images数组存在
    const images = formData.images || [];
    
    if (images.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <ImageIcon size={48} className="mx-auto mb-2 text-gray-300" />
          <p>暂无图片，请上传产品图片</p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {images.map((image, index) => {
          // 优先使用缩略图URL，如果不存在则使用原图URL
          // 注意：后端返回的是image_url字段，不是url字段
          const imageUrl = image?.thumbnail_url || image?.image_url;
          
          return (
            <div 
              key={index} 
              className={`relative border rounded-lg overflow-hidden group ${
                index === formData.mainImageIndex ? 'border-blue-500 border-2' : 'border-gray-200'
              }`}
            >
              {/* 图片缩略图 */}
              <div className="aspect-square bg-gray-100 flex items-center justify-center">
                {imageUrl ? (
                  <img 
                    src={imageUrl} 
                    alt={`产品图片 ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      // 如果缩略图加载失败，尝试使用原图
                      if (imageUrl !== image.image_url && image.image_url) {
                        e.target.src = image.image_url;
                      } else {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNlNWU1ZTUiLz48cGF0aCBkPSJNNjUgMzVINTVWNTVIzVWNjVINTVWODVINjVWNjVINjVWNTVIODVWNDVINjVWMzVaTTY1IDY1SDc1Vjc1SDY1VjY1WiIgZmlsbD0iIzk5OSIvPjwvc3ZnPg==';
                      }
                    }}
                  />
                ) : (
                  <div className="text-gray-400">
                    <ImageIcon size={32} />
                  </div>
                )}
              </div>
              
              {/* 图片操作按钮 */}
              <div className="absolute top-2 right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {/* 设置主图按钮 */}
                <button
                  type="button"
                  onClick={() => setMainImage(index)}
                  className={`p-1 rounded-full ${
                    index === formData.mainImageIndex 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                  title={index === formData.mainImageIndex ? '当前主图' : '设为主图'}
                >
                  <Star size={14} fill={index === formData.mainImageIndex ? 'currentColor' : 'none'} />
                </button>
                
                {/* 删除按钮 */}
                <button
                  type="button"
                  onClick={() => deleteImage(index)}
                  className="p-1 rounded-full bg-white text-red-600 hover:bg-red-50"
                  title="删除图片"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              
              {/* 移动按钮 */}
              <div className="absolute bottom-2 right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  type="button"
                  onClick={() => moveImageUp(index)}
                  disabled={index === 0}
                  className={`p-1 rounded-full ${
                    index === 0 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                  title="上移"
                >
                  <MoveUp size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => moveImageDown(index)}
                  disabled={index === (formData?.images?.length || 0) - 1}
                  className={`p-1 rounded-full ${
                    index === (formData?.images?.length || 0) - 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                  title="下移"
                >
                  <MoveDown size={14} />
                </button>
              </div>
              
              {/* 图片信息 */}
              <div className="p-2 bg-white bg-opacity-90">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-600 truncate">
                    {image.name || `图片 ${index + 1}`}
                  </span>
                  {index === formData.mainImageIndex && (
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                      主图
                    </span>
                  )}
                </div>
                {image.size && (
                  <div className="text-xs text-gray-500 mt-1">
                    {(image.size / 1024).toFixed(1)} KB
                  </div>
                )}
                {/* 显示缩略图标记 */}
                {image.thumbnail_url && (
                  <div className="text-xs text-green-600 mt-1">
                    已生成缩略图
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">
          {isEditing ? '编辑产品' : '添加新产品'}
        </h2>
        <div className="flex items-center space-x-4">
          {renderAutoSaveStatus()}
          {draftId && (
            <div className="text-sm text-gray-500">
              草稿ID: <span className="font-mono">{draftId.slice(0, 8)}...</span>
            </div>
          )}
        </div>
      </div>

      <form onSubmit={(e) => handleSubmit(e, false)}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 左侧：基本信息 */}
          <div className="lg:col-span-2 space-y-6">
            {/* 产品名称 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                产品名称 *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="请输入产品名称"
                required
              />
            </div>

            {/* 简短描述 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                简短描述 *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="请输入产品简短描述"
                required
              />
            </div>

            {/* 完整描述 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                完整描述
              </label>
              <ReactQuill
                value={formData.full_description}
                onChange={(value) => handleRichTextChange(value, 'full_description')}
                modules={quillModules}
                formats={quillFormats}
                className="h-64 mb-12"
              />
            </div>

            {/* 价格信息 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                价格信息
              </label>
              <ReactQuill
                value={formData.price_info}
                onChange={(value) => handleRichTextChange(value, 'price_info')}
                modules={quillModules}
                formats={quillFormats}
                className="h-32 mb-12"
                placeholder="请输入价格信息，如：面议、具体价格等"
              />
            </div>

            {/* 产品特性 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                产品特性
              </label>
              <ReactQuill
                value={formData.features}
                onChange={(value) => handleRichTextChange(value, 'features')}
                modules={quillModules}
                formats={quillFormats}
                className="h-32 mb-12"
                placeholder="请输入产品特性"
              />
            </div>

            {/* 技术规格 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                技术规格
              </label>
              <ReactQuill
                value={formData.specifications}
                onChange={(value) => handleRichTextChange(value, 'specifications')}
                modules={quillModules}
                formats={quillFormats}
                className="h-32 mb-12"
                placeholder="请输入技术规格"
              />
            </div>

            {/* 应用领域 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                应用领域
              </label>
              <ReactQuill
                value={formData.applications}
                onChange={(value) => handleRichTextChange(value, 'applications')}
                modules={quillModules}
                formats={quillFormats}
                className="h-32 mb-12"
                placeholder="请输入应用领域"
              />
            </div>

            {/* 技术支持 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                技术支持与服务
              </label>
              <ReactQuill
                value={formData.support_info}
                onChange={(value) => handleRichTextChange(value, 'support_info')}
                modules={quillModules}
                formats={quillFormats}
                className="h-32 mb-12"
                placeholder="请输入技术支持与服务信息"
              />
            </div>
          </div>

          {/* 右侧：图片上传和操作 */}
          <div className="space-y-6">
            {/* 图片上传区域 */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
              <input
                type="file"
                id="image-upload"
                multiple
                accept="image/*"
                onChange={handleImagesUpload}
                className="hidden"
                disabled={uploading}
              />
              <label
                htmlFor="image-upload"
                className={`cursor-pointer flex flex-col items-center justify-center ${
                  uploading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <Upload size={48} className="text-gray-400 mb-4" />
                <p className="text-lg font-medium text-gray-700 mb-2">
                  {uploading ? '上传中...' : '点击上传产品图片'}
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  支持 JPG、PNG、GIF 格式，最多10张图片
                </p>
                <div className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                  {uploading ? '上传中...' : '选择图片'}
                </div>
              </label>
            </div>

            {/* 图片缩略图显示 */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-700">
                  产品图片 ({formData?.images?.length || 0}/10)
                </h3>
                {formData?.images?.length > 0 && (
                  <div className="text-sm text-gray-600">
                    主图: <span className="font-medium">第 {(formData.mainImageIndex || 0) + 1} 张</span>
                  </div>
                )}
              </div>
              {renderImageThumbnails()}
            </div>

            {/* 联系方式图片上传 */}
            <div>
              <h3 className="text-lg font-medium text-gray-700 mb-3">联系方式二维码</h3>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-500 transition-colors">
                <input
                  type="file"
                  id="contact-image-upload"
                  accept="image/*"
                  onChange={handleContactImageUpload}
                  className="hidden"
                  disabled={uploading}
                />
                {formData.contact_image ? (
                  <div className="flex flex-col items-center">
                    <img
                      src={formData.contact_image}
                      alt="联系方式二维码"
                      className="w-24 h-24 object-contain mb-2 rounded border"
                    />
                    <div className="flex space-x-2">
                      <label
                        htmlFor="contact-image-upload"
                        className={`px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 cursor-pointer ${
                          uploading ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        更换图片
                      </label>
                      <button
                        type="button"
                        onClick={removeContactImage}
                        className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
                      >
                        移除
                      </button>
                    </div>
                  </div>
                ) : (
                  <label
                    htmlFor="contact-image-upload"
                    className={`cursor-pointer flex flex-col items-center justify-center ${
                      uploading ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <Upload size={24} className="text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600 mb-2">上传二维码图片（30x30）</p>
                    <div className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
                      {uploading ? '上传中...' : '选择图片'}
                    </div>
                  </label>
                )}
              </div>
            </div>

            {/* 状态选择 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                产品状态
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="draft">草稿</option>
                <option value="published">已发布</option>
                <option value="unpublished">未发布</option>
              </select>
            </div>

            {/* 操作按钮 */}
            <div className="space-y-4 pt-6 border-t border-gray-200">
              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={uploading}
                  className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  <Save size={18} className="mr-2" />
                  {uploading ? '保存中...' : '保存草稿'}
                </button>
                
                <button
                  type="button"
                  onClick={(e) => handleSubmit(e, true)}
                  disabled={uploading || !formData.name || !formData.description}
                  className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? '发布中...' : '立即发布'}
                </button>
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handlePreview}
                  disabled={!formData.name}
                  className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  <Eye size={18} className="mr-2" />
                  预览产品
                </button>
                
                <button
                  type="button"
                  onClick={onCancel}
                  className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  取消
                </button>
              </div>

              {/* 草稿管理 */}
              {draftId && (
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                      上次保存: {lastSaved ? lastSaved.toLocaleString() : '从未保存'}
                    </div>
                    <button
                      type="button"
                      onClick={handleClearDraft}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      清除草稿
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProductEditor;
